import type { File as FileRecord } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { formatTitleCase } from '@efdigital/ll-addon-ui/helpers/title-case';
import { createFile } from '@efdigital/ll-admin-v2/data/builders/content/files';
import { getFileIcon } from '@efdigital/ll-admin-v2/lib/file-icon';
import { formatFileSize } from '@efdigital/ll-admin-v2/utils/files';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface Signature {
  Args: {
    parentFolderId: string;
    onFileUploaded: (file: FileRecord) => void;
  };
}

export default class FileUploader extends Component<Signature> {
  @service declare store: Store;

  @tracked fileRecord: FileRecord | null = null;
  @tracked size: number = 0;
  @tracked isDragging = false;
  @tracked isUploading = false;
  @tracked error: string | null = null;

  handleDragOver = (event: DragEvent): void => {
    event.preventDefault();
    this.isDragging = true;
  };

  handleDragLeave = (event: DragEvent): void => {
    event.preventDefault();
    this.isDragging = false;
  };

  handleDrop = (event: DragEvent): void => {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    const file = files?.[0];
    if (file) {
      void this.handleFileSelect(file);
    }
  };

  handleFileInput = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      void this.handleFileSelect(file);
    }
  };

  setFileName = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    let value = target.value;
    // If the value does not end with the original file extension,
    // or ends with a different file extension, update the file extension.
    const originalExtension = this.fileRecord!.name.split('.').pop();
    if (originalExtension) {
      if (!value.endsWith(originalExtension)) {
        value = `${value.substring(0, value.lastIndexOf('.'))}.${originalExtension}`;
      }
    }
    // Replace any non-alphanumeric characters with a hyphen
    value = value.replaceAll(/[^a-zA-Z0-9._-]+/g, '-');
    this.fileRecord!.name = value;
  };

  setTitle = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    this.fileRecord!.title = target.value;
  };

  setDescription = (event: Event): void => {
    const target = event.target as HTMLTextAreaElement;
    this.fileRecord!.description = target.value;
  };

  async handleFileSelect(file: globalThis.File): Promise<void> {
    this.size = file.size;
    this.fileRecord = this.store.createRecord<FileRecord>(
      DataTypeContent.File,
      {
        name: file.name,
        contentType: file.type,
        title: formatTitleCase(
          file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
        ),
        description: '',
        dataUri: await this.readFileAsDataUri(file),
        parent: this.args.parentFolderId,
      }
    );
    this.error = null;
  }

  removeFile = (): void => {
    this.fileRecord!.dataUri = ''; // Clear the data URI to free up memory first
    this.fileRecord = null;
    this.error = null;
  };

  uploadFile = async (event: Event): Promise<void> => {
    event.preventDefault();

    try {
      this.isUploading = true;
      this.error = null;

      const createFileRequest = createFile(this.fileRecord!, this.store);
      await this.store.request(createFileRequest);

      this.args.onFileUploaded(this.fileRecord!);
    } catch (error) {
      console.error(error);
      this.error = error instanceof Error ? error.message : 'Upload failed';
    } finally {
      this.isUploading = false;
    }
  };

  private readFileAsDataUri(file: globalThis.File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  <template>
    <div class="file-uploader">
      {{#if this.fileRecord}}
        <form {{on "submit" this.uploadFile}}>
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Selected File</h5>
            </div>
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="fs-1 text-secondary me-3">
                  <iconify-icon
                    icon={{getFileIcon this.fileRecord.contentType}}
                  />
                </div>
                <div class="flex-grow-1">
                  <small class="text-muted">
                    {{formatFileSize this.size}}
                  </small>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  {{on "click" this.removeFile}}
                >
                  <iconify-icon inline icon="ph:x-bold" />
                  Change File
                </button>
              </div>

              <div class="mb-3">
                <label for="file-name" class="form-label">File Name</label>
                <input
                  type="text"
                  class="form-control"
                  id="file-name"
                  value={{this.fileRecord.name}}
                  required
                  {{on "change" this.setFileName}}
                />
              </div>

              <div class="mb-3">
                <label for="file-title" class="form-label">Title (optional)</label>
                <input
                  type="text"
                  class="form-control"
                  id="file-title"
                  value={{this.fileRecord.title}}
                  {{on "change" this.setTitle}}
                />
              </div>

              <div class="mb-3">
                <label for="file-description" class="form-label">Description
                  (optional)</label>
                <textarea
                  class="form-control"
                  id="file-description"
                  rows="3"
                  value={{this.fileRecord.description}}
                  {{on "change" this.setDescription}}
                ></textarea>
              </div>
            </div>
          </div>

          {{#if this.error}}
            <div class="alert alert-danger">
              {{this.error}}
            </div>
          {{/if}}

          <div class="d-flex gap-2">
            <button
              type="submit"
              class="btn btn-primary"
              disabled={{this.isUploading}}
            >
              {{#if this.isUploading}}
                <span class="spinner-border spinner-border-sm me-2"></span>
                Uploading...
              {{else}}
                <iconify-icon inline icon="ph:upload-bold" />
                Upload File
              {{/if}}
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              {{on "click" this.removeFile}}
              disabled={{this.isUploading}}
            >
              Cancel
            </button>
          </div>
        </form>
      {{else}}
        <div
          class="card border-2 border-dashed rounded text-bg-light
            {{if this.isDragging 'border-primary bg-primary-subtle'}}"
          {{on "dragover" this.handleDragOver}}
          {{on "dragleave" this.handleDragLeave}}
          {{on "drop" this.handleDrop}}
        >
          <div class="card-body text-center py-5">
            <div class="mb-3 fs-1">
              <iconify-icon icon="ph:file-arrow-up-duotone" />
            </div>
            <h5 class="card-title">Upload a file</h5>
            <p class="card-text text-muted mb-4">
              Select a file to upload to this folder
            </p>
            <label class="btn btn-primary" for="file-input">
              <iconify-icon inline icon="ph:file-plus-bold" />
              Select File
            </label>
            <input
              type="file"
              id="file-input"
              class="d-none"
              {{on "change" this.handleFileInput}}
            />
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
